import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { NEVER, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import moment from 'moment';
import { StorageService } from 'ngx-webstorage-service';

import { environment } from '@environment';

import { LoadingSpinnerService } from '../loading-spinner/service/loading-spinner.service';
import { PhxConstants } from '../model';
import { AppStorageService } from '../services/app-storage.service';
import { ConnectivityService } from '../services/connectivity.service';
import { TokenRefreshService } from '../services/token-refresh.service';

import HTTPResponseStatus = PhxConstants.HTTPResponseStatus;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly endpointsToIgnoreAuthError: string[] = ['Account/Logout'];
  private readonly refreshTokenErrorKeys: string[] = ['invalid_grant', 'invalid_clientId'];

  constructor(
    private tokenRefreshService: TokenRefreshService,
    private connectivityService: ConnectivityService,
    private loadingSpinnerService: LoadingSpinnerService,
    @Inject(AppStorageService) private storageService: StorageService
  ) {}

  private static addDefaultHeaders(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return request;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const bearerToken = this.storageService.get('BearerToken');
    const bearerTokenExpiresOn = this.storageService.get('BearerTokenExpiresOn');

    const twoMinutesFromNow = moment().add(2, 'm');
    const getTokenCall = req.url.indexOf('token') !== -1;
    const isApiRequest = req.url.startsWith(environment.apiUrl);
    // A login attempt (grant_type=password) against an empty session has nothing for
    // logoutAndRedirect() to tear down — skipping it here just avoids the hard reload that
    // wipes the caller's own rejected-credentials error before it can render. The `!bearerToken`
    // check keeps this scoped to that case: LoginComponent clears storage before calling
    // AuthService.login(), but the SSO and registration callers of the same method do not, so a
    // rejected attempt there can still have a real prior session in storage — that case must
    // keep falling through to logoutAndRedirect() exactly as before, so it still gets cleared.
    const isLoginAttempt = getTokenCall && !bearerToken && typeof req.body === 'string' && req.body.includes('grant_type=password');

    if (!getTokenCall && !this.tokenRefreshService.preemptiveSuppressed && bearerTokenExpiresOn && moment(bearerTokenExpiresOn) < twoMinutesFromNow) {
      const response$ = this.refreshAndRetry(req, next);
      return isApiRequest ? this.reportOutcome(response$) : response$;
    }

    const response$ = next.handle(TokenInterceptor.addDefaultHeaders(req, bearerToken)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === HTTPResponseStatus.Unauthorized && !isLoginAttempt && !this.endpointsToIgnoreAuthError.some(endpoint => req.url.includes(endpoint))) {
            const refreshToken = this.storageService.get('RefreshToken');

            if (refreshToken && bearerToken) {
              this.tokenRefreshService.preemptiveSuppressed = false;
              return this.refreshAndRetry(req, next);
            }

            // No refresh token to fall back on — the session can't be salvaged.
            return this.abandonExpiredSession();
          }

          if (err.status === HTTPResponseStatus.BadRequest && !isLoginAttempt && err.error && this.refreshTokenErrorKeys.includes(err.error.error)) {
            this.tokenRefreshService.logoutAndRedirect();
          }
        }

        return throwError(() => err);
      })
    );

    // Only requests to our own API say anything about FBO's connectivity. A successful
    // third-party/asset request shouldn't clear an offline state, and an unrelated CORS/network
    // failure to some other origin shouldn't raise one.
    return isApiRequest ? this.reportOutcome(response$) : response$;
  }

  private refreshAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.tokenRefreshService.ensureFreshToken().pipe(
      switchMap(token => next.handle(TokenInterceptor.addDefaultHeaders(request, token))),
      // A 401 on the retried request means the refresh couldn't salvage the session either —
      // the refresh token has expired alongside the access token. The catchError in intercept()
      // wraps only the original request, so without this the retry's error reaches the caller.
      catchError(err =>
        err instanceof HttpErrorResponse && err.status === HTTPResponseStatus.Unauthorized ? this.abandonExpiredSession() : throwError(() => err)
      )
    );
  }

  // logoutAndRedirect() already covers the user-facing side (toast + redirect to login) through
  // a hard document.location.replace(), so nothing is left to observe this request. NEVER
  // rather than EMPTY because a promise-based caller (lastValueFrom/firstValueFrom) turns an
  // empty completion into an EmptyError, which surfaces as an application error in place of the
  // expired session it stands for. NEVER also means this request's own finalize()/catchError
  // never runs to hide its spinner, so hide it here — logoutAndRedirect() must not, because the
  // ensureFreshToken()/performRefresh() callers reach it while still completing via EMPTY and
  // hiding through their own finalize(), which would send showCount negative.
  private abandonExpiredSession(): Observable<never> {
    this.tokenRefreshService.logoutAndRedirect();
    this.loadingSpinnerService.hideAll();
    return NEVER;
  }

  // Reports every real request's outcome to ConnectivityService so active usage keeps
  // connectivity status current without a dedicated probe. Any status — including 4xx/5xx —
  // means the server answered, so it counts as reachable. status === 0 means only that the
  // browser had no response to give: a dropped connection, but equally a cross-origin response
  // whose CORS headers were missing, or a request cancelled by navigating away, both of which
  // leave the API perfectly healthy. Too weak a signal to declare an outage on, so it asks
  // ConnectivityService to probe rather than reporting unreachable itself.
  private reportOutcome(source: Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
    return source.pipe(
      tap(event => {
        // next.handle() emits a Sent event before the eventual Response; only the latter
        // confirms the server actually answered.
        if (event.type === HttpEventType.Response) {
          this.connectivityService.reportReachable(true);
        }
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 0) {
          this.connectivityService.verifyReachability();
        } else {
          this.connectivityService.reportReachable(true);
        }
        return throwError(() => err);
      })
    );
  }
}
